import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { UserType } from 'api/types';
import { Button } from 'shared/components/Button/Button';
import { AccountIcon } from 'shared/components/icons/AccountIcon';
import { PhoneIcon } from 'shared/components/icons/PhoneIcon';
import { EmailIcon } from 'shared/components/icons/EmailIcon';
import { formatPhoneNumber } from 'shared/utils/formatPhoneNumber';
import { HomeIcon } from 'shared/components/icons/HomeIcon';
import { Name } from '../AccountBody/AccountBody.styles';
import {
  ContactInfoBodyHeader,
  ContentBody,
  ItemsWrapper,
  ItemTitle,
  TitleIconContainer,
  Value,
  ValueButtonContainer,
} from './ContactInfoBody.styles';

type Props = {
  user: UserType;
  onClickEditPhone: () => void;
  onClickEditAddress: () => void;
};

export function ContactInfoBody({ user, onClickEditPhone, onClickEditAddress }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const items = useMemo(
    () => [
      {
        title: t('account.name'),
        value: `${user.first_name} ${user.last_name}`,
        icon: <AccountIcon color={theme.colors.textSecondarySubtleGrey80} filled={false} />,
      },
      {
        title: t('account.email'),
        value: user.email,
        icon: <EmailIcon color={theme.colors.textSecondarySubtleGrey80} />,
      },
      {
        title: t('account.phoneNumber'),
        value: formatPhoneNumber(user.phone.phone_number),
        icon: <PhoneIcon color={theme.colors.textSecondarySubtleGrey80} />,
        onClick: onClickEditPhone,
      },
      {
        title: t('accountMenu.billingAddress'),
        value: (
          <>
            {user.address.street_1} {user.address.street_2} <br /> {user.address.city},
            {user.address.state} {user.address.zip_code}
          </>
        ),
        icon: <HomeIcon color={theme.colors.textSecondarySubtleGrey80} filled={false} />,
        onClick: onClickEditAddress,
      },
    ],
    [onClickEditAddress, onClickEditPhone, t, theme.colors.textSecondarySubtleGrey80, user],
  );

  return (
    <ContentBody>
      <ContactInfoBodyHeader>
        <Name as="h3">{t('accountMenu.contactInformation')}</Name>
      </ContactInfoBodyHeader>

      <ItemsWrapper>
        {items.map(({ title, value, icon, onClick }) => (
          <div key={title}>
            <TitleIconContainer>
              {icon}
              <ItemTitle as="p">{title}</ItemTitle>
            </TitleIconContainer>

            <ValueButtonContainer>
              <Value as="p" $colorBlue={!!onClick}>
                {value}
              </Value>
              {onClick && <Button styleVariant="tertiary" label="Edit" onClick={onClick} />}
            </ValueButtonContainer>
          </div>
        ))}
      </ItemsWrapper>
    </ContentBody>
  );
}

export default ContactInfoBody;
